/**
 * DropdownHelpManager - Lobby Dropdown Interactive Documentation & Modals
 * Encapsulates descriptive metadata for all lobby dropdowns, interactive detail cards,
 * card-back pattern previews, and label info icon injection.
 */

export const DROPDOWN_DESCRIPTIONS = {
  optPlayerCount: {
    title: 'Players & Seating',
    subtitle: 'Choose from 2 to 8 players with individual cutthroat, shifting partnerships, or fixed teams:',
    options: [
      {
        val: '2',
        name: '2 Players (Head-to-head)',
        desc: 'Traditional cribbage. 6 cards dealt (or 9 in Super Deal), discard 2 to the crib. Head-to-head pegging and scoring on 2 concentric tracks.'
      },
      {
        val: '3_indiv',
        name: '3 Players (Individual / Cutthroat)',
        desc: 'Official 3-player cribbage: 5 cards dealt to each player plus 1 card dealt directly to the crib. Each player discards 1 card to complete the 4-card crib. 3 concentric serpentine board tracks.'
      },
      {
        val: '3_shifting',
        name: '3 Players (Shifting Partnerships — 1 vs 2)',
        desc: 'Dealer plays alone and always scores the crib! The other 2 players team up into a pair for the hand and both score when either pegs or counts.'
      },
      {
        val: '4_indiv',
        name: '4 Players (Individual / Cutthroat)',
        desc: 'Every player for themselves! 5 cards dealt each; each player discards 1 card to form the 4-card crib. 4 separate concentric tracks race to the finish.'
      },
      {
        val: '4_part',
        name: '4 Players (Partnership — 2 vs 2)',
        desc: 'Partners sit opposite each other (Seats 1 & 3 vs Seats 2 & 4). 5 cards dealt each, 1 discard each to crib. Scores pool into shared team tracks!'
      },
      {
        val: '5_shifting',
        name: '5 Players (Shifting Partnerships — 1 vs 2 Pairs)',
        desc: 'Dealer plays alone and always scores the crib! The other 4 players form 2 shifting pairs across the table and peg forward together.'
      },
      {
        val: '6_teams',
        name: '6 Players (3 Teams of 2)',
        desc: '3 teams of 2 players (Seats 1 & 4 vs Seats 2 & 5 vs Seats 3 & 6). 5 cards dealt each, 1 discard to the crib. 3 team tracks on board.'
      },
      {
        val: '7_shifting',
        name: '7 Players (Shifting Partnerships — 1 vs 3 Pairs)',
        desc: 'Dealer plays alone and always scores the crib! The other 6 players form 3 shifting pairs across the table.'
      },
      {
        val: '8_teams',
        name: '8 Players (4 Teams of 2)',
        desc: '4 teams of 2 players (Seats 1 & 5 vs Seats 2 & 6 vs Seats 3 & 7 vs Seats 4 & 8). 5 cards dealt each, 1 discard to the crib. 4 team tracks on board.'
      }
    ]
  },
  optOpp: {
    title: 'Players',
    subtitle: 'Choose how many players and devices to play with:',
    options: [
      {
        val: 'cpu',
        name: 'You vs computer',
        desc: 'Play single-player against the Crazy Cribbage AI engine. Choose from multiple distinct computer personalities below.'
      },
      {
        val: 'online',
        name: 'Online — two devices',
        desc: 'Play head-to-head across two devices (phones, tablets, or computers) via peer-to-peer connection with an 8-character room code. No login required.'
      }
    ]
  },
  optAiProfile: {
    title: 'Computer Opponent Skill Levels & Personalities',
    subtitle: 'Select the skill level and playing style for your computer opponent:',
    options: [
      {
        val: 'shark',
        name: 'Expert — The Shark (EV / GTO Optimal)',
        desc: 'Expert level. Plays mathematically optimal Game Theory Optimal (GTO) cribbage. Analyzes discard permutations and pegging rollouts for maximum expected point value.'
      },
      {
        val: 'master',
        name: 'Expert — The Grandmaster (Tournament Precision)',
        desc: 'Expert level. High-level tournament champion with deep board-position awareness, disciplined crib defense, and precision endgame play.'
      },
      {
        val: 'turtler',
        name: 'Advanced — The Turtler (Defensive Strategy)',
        desc: 'Advanced level. Risk-averse, highly defensive play style. Sacrifices hand potential to starve opponent cribs of synergy and carefully controls pegging counts.'
      },
      {
        val: 'maverick',
        name: 'Advanced — The Maverick (Tactical Pressure)',
        desc: 'Advanced level. Aggressive pegging pressure, setting up run sequences, pair traps, and forcing opponents into tough Go decisions.'
      },
      {
        val: 'gambler',
        name: 'Intermediate — The Gambler (Aggressive / Big Hands)',
        desc: 'Intermediate level. High-variance play style. Chases massive hands and explosive combos, taking risks on discards and aggressively pairing cards.'
      },
      {
        val: 'regular',
        name: 'Intermediate — The Regular (Steady Fundamentals)',
        desc: 'Intermediate level. Consistent, dependable cribbage following time-tested counting principles, balanced crib discards, and safe leads.'
      },
      {
        val: 'rookie',
        name: 'Beginner — The Rookie (Friendly & Relaxed)',
        desc: 'Beginner level. Friendly, casual opponent focused on straightforward counting. Great for newcomers and relaxed games without aggressive traps.'
      },
      {
        val: 'casual',
        name: 'Beginner — The Casual (Easygoing / Learning)',
        desc: 'Beginner level. Easygoing play style that avoids complex traps, making simple and accessible card choices to keep games fun and welcoming.'
      }
    ]
  },
  optPegStyle: {
    title: 'Custom Peg Styles & Trail FX',
    subtitle: 'Choose custom 3D sculpted peg heads and hopping particle effects:',
    options: [
      {
        val: 'classic',
        name: 'Classic — Turned Finial & Avatar (Default)',
        desc: 'Traditional turned hardwood/metallic pin with custom circular avatar medallions.'
      },
      {
        val: 'crown',
        name: 'Gold Crown — Royal Coronet & Gemstones',
        desc: 'Royal golden 5-peak crown topper with embedded ruby and sapphire jewels that glisten under 3D table lights.'
      },
      {
        val: 'skull',
        name: 'Pirate Skull — Silver Buccaneer & Carved Sockets',
        desc: 'Burnished pirate skull topper with carved eye sockets, skull brow, and silver sheen.'
      },
      {
        val: 'beermug',
        name: 'Pint Beer Mug — Frosted Tankard & Foam Cap',
        desc: 'Frosted pub tankard filled with amber ale, frothy white foam head, and curved glass handle.'
      },
      {
        val: 'rivet',
        name: 'Brass Rivet — Machined Industrial Hex Bolt',
        desc: 'Machined hex-head industrial machine bolt with heavy beveled facets and metallic sheen.'
      },
      {
        val: 'crystal',
        name: 'Neon Crystal — Prismatic Radiant Gem',
        desc: 'Prismatic faceted gemstone topper with radiant emissive glow and colorful edge highlights.'
      }
    ]
  },
  optTableEnvironment: {
    title: '3D Table Environments & Backdrops',
    subtitle: 'Select 3D themed table surroundings matching your game mood:',
    options: [
      {
        val: 'auto',
        name: 'Auto — Brand Themed (Default)',
        desc: 'Automatically matches the active game brand: The Pub for Pints & Pegs, Vegas High Roller Suite for Crazy Cribbage, The Thunderdome for Cribbage Clash, and Velvet Lounge for Skunked & Stripped.'
      },
      {
        val: 'pub',
        name: 'The Pub — Oak Planks, Pint Coasters & Candle Warmth',
        desc: 'Dimmed tavern ambiance with aged tavern oak planks, beer coasters, and cozy amber lighting.'
      },
      {
        val: 'vegas',
        name: 'Vegas High Roller Suite — Green Baize & Mahogany',
        desc: 'Luxury casino VIP suite with emerald casino baize, gold card-suit watermarks, rich polished mahogany trim, and neon skyline glow.'
      },
      {
        val: 'cabin',
        name: 'Mountain Lodge — Pine Logs & Stone Hearth',
        desc: 'Rustic log cabin interior with knotty pine floors, fieldstone fireplace wall, and cozy amber mountain glow.'
      },
      {
        val: 'thunderdome',
        name: 'The Thunderdome / Raceway — Tarmac & Hazard Lines',
        desc: 'High-octane raceway setting with textured asphalt tarmac, burnout tire tracks, hazard warning chevrons, and stadium floodlights.'
      },
      {
        val: 'velvet',
        name: 'Velvet Lounge — Royal Emerald Baize',
        desc: 'Moody speakeasy lounge with deep emerald baize and satin gold finishes.'
      },
      {
        val: 'studio',
        name: 'Studio Wood — Nordic Ash Minimalist',
        desc: 'Clean Scandinavian studio setting with pale ash timber grain and soft diffuse lighting.'
      },
      {
        val: 'none',
        name: 'None — Minimalist Backdrop',
        desc: 'Minimalist shadow surface without textured table borders.'
      }
    ]
  },
  optBoardView: {
    title: 'Board & Room View Modes',
    subtitle: 'Choose your visual presentation and immersion style:',
    options: [
      {
        val: 'room3d-fullscreen',
        name: '3D Room (Full Screen) — Edge-to-Edge Cinematic Room',
        desc: 'Launches into edge-to-edge full screen mode featuring realistic themed 3D rooms (Pub, Vegas Suite, Mountain Lodge, Velvet Speakeasy, Industrial, or Nordic Studio), table, chairs, and cribbage board for maximum visual immersion.'
      },
      {
        val: 'room3d',
        name: '3D Room (Full Width) — Complete Themed Tabletop & Surroundings',
        desc: 'Full-width cinematic view featuring realistic themed 3D rooms (Pub, Vegas Suite, Mountain Lodge, Velvet Speakeasy, Industrial, or Nordic Studio), table, chairs, and cribbage board.'
      },
      {
        val: '3d',
        name: '3D Board — Interactive Babylon.js Cribbage Board',
        desc: 'Standard split-table view with the interactive 3D board, smooth track zoom, and cinematic action cameras.'
      },
      {
        val: '2d',
        name: 'Flat Board — Classic Vector SVG Board',
        desc: 'Traditional top-down 2D flat track view with crisp vector graphics.'
      },
      {
        val: 'none',
        name: 'None — Scorecard & Cards Only',
        desc: 'Maximizes card area by hiding the track board entirely while scores track via header chips.'
      }
    ]
  },
  optActionCam: {
    title: 'Cinematic Action Cam Replays',
    subtitle: 'Automated dynamic slow-motion camera zoom and orbit during major match moments:',
    options: [
      {
        val: 'yes',
        name: 'Yes — Cinematic orbit on 24+ pts, 4-of-a-kind & game win (Default)',
        desc: 'Automatically launches a broadcast-style slow-motion 3D camera orbit with a recording HUD badge whenever a player scores 24+ points, hits a 4-of-a-kind (double pair royal) during pegging, or pegs into the winning game hole (121 / 481).'
      },
      {
        val: 'no',
        name: 'No — Static camera view',
        desc: 'Maintains player camera position during high-scoring events without automated cinematic replays.'
      }
    ]
  },
  optCardAssassin: {
    title: 'Card Assassin Animations',
    subtitle: 'Cinematic duel animation when a player overtakes an opponent on the board:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show overtake animations (Default)',
        desc: 'Displays an action-packed cinematic showdown featuring animated cards battling with legendary weapons whenever one player overtakes another on the board.'
      },
      {
        val: 'no',
        name: 'No — skip overtake animations',
        desc: 'Disables the Card Assassin overtake animation for faster match pacing.'
      }
    ]
  },
  optJailAnimation: {
    title: 'Jailhouse Mugshot Animations',
    subtitle: 'Cinematic cartoon animation when a player lands in Jail:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show jail mugshot animations (Default)',
        desc: 'Displays a cinematic cartoon sequence where iron prison bars slam down, a burly police guard card marches in, slaps a striped convict uniform on the player card with frantic kicking legs, and snaps a mugshot with camera flash.'
      },
      {
        val: 'no',
        name: 'No — skip jail animations',
        desc: 'Disables the Jailhouse Mugshot animation for faster game pacing.'
      }
    ]
  },
  optBarAnimation: {
    title: 'Saloon Bar Slide Animations',
    subtitle: 'Cinematic cartoon animation when a player lands on Bar or Drink spaces:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show saloon bar slide animations (Default)',
        desc: 'Displays a cinematic cartoon sequence where wooden saloon swinging doors kick open, an overflowing frothy beer mug slides across the counter and bumps the card, the card chugs it with spinning spiral eyes and wobbly noodle IK legs, and collapses face-first onto a bar stool while bubbles pop.'
      },
      {
        val: 'no',
        name: 'No — skip saloon bar animations',
        desc: 'Disables the Saloon Bar Slide animation for faster match pacing.'
      }
    ]
  },
  optCannonAnimation: {
    title: 'Jump (ACME Cannonball) Animations',
    subtitle: 'Cinematic cartoon animation when a player lands on a Jump Spring or Start tile:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show ACME cannonball animations (Default)',
        desc: 'Displays a cinematic cartoon sequence where an ACME-style striped cannon rolls out, the card climbs inside rear-first with feet kicking out the muzzle, a sparkling fuse counts down to KABOOM, and blasts the card in a high parabolic flight across the tracks before lawn-darting head-first into the destination hole with spinning dizzy stars.'
      },
      {
        val: 'no',
        name: 'No — skip cannonball animations',
        desc: 'Disables the ACME Human Cannonball animation for faster match pacing.'
      }
    ]
  },
  optSpeedTrapAnimation: {
    title: 'Stop Sign (Speed Trap Radar) Animations',
    subtitle: 'Cinematic cartoon interception when a player is stopped by a Stop Sign or blocked in a No Passing Lane:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show speed trap radar animations (Default)',
        desc: 'Displays a cinematic sequence where emergency red and blue flashers strobe the screen, an animated radar sweep tracks offender velocity, a motorcycle cop card in aviator sunglasses zips in across asphalt, signals HALT with a police whistle, and slaps an official speeding citation onto the player\'s card face.'
      },
      {
        val: 'no',
        name: 'No — skip speed trap animations',
        desc: 'Disables the Speed Trap Radar animation for faster match pacing.'
      }
    ]
  },
  optDetourAnimation: {
    title: 'Detour Animations',
    subtitle: 'Cinematic cartoon sequence when a player lands on a Detour (🚧) tile:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show GPS detour animations (Default)',
        desc: 'Displays an interactive in-car dashboard navigation display with digital speedometer and GPS route alerts. As the GPS warns of road work ahead, the convertible swerves down a bumpy dirt road past construction barricades with bouncing suspension, dust clouds, and an old-school ahooga horn.'
      },
      {
        val: 'no',
        name: 'No — skip detour animations',
        desc: 'Disables the Detour scenic route animation for faster match pacing.'
      }
    ]
  },
  optRoundAboutAnimation: {
    title: 'Round About Rotary Animations',
    subtitle: 'Cinematic cartoon sequence when a player lands on a Round About (🔄) tile:',
    options: [
      {
        val: 'yes',
        name: 'Yes — show GPS rotary animations (Default)',
        desc: 'Displays an interactive in-car dashboard navigation display with digital speedometer and GPS route alerts. The GPS announces the missed rotary exit while the player card orbits the rotary with tire squeals, slide whistles, and dizzy spiral eyes.'
      },
      {
        val: 'no',
        name: 'No — skip round about animations',
        desc: 'Disables the Round About rotary animation for faster match pacing.'
      }
    ]
  },
  optScenicRouteAnimation: {
    title: 'Scenic Route & Rotary Animations',
    subtitle: 'Cinematic cartoon sequence when a player lands on a Round About (🔄) or Detour (🚧):',
    options: [
      {
        val: 'yes',
        name: 'Yes — show GPS scenic route & rotary animations (Default)',
        desc: 'Displays an interactive in-car dashboard navigation display with digital speedometer and GPS route alerts. For Round Abouts, the GPS announces the rotary while the player card orbits the rotary with tire squeals and dizzy spiral eyes. For Detours, the GPS warns of road work ahead as the convertible swerves down a bumpy dirt road with bouncing suspension, dust clouds, and an old-school ahooga horn.'
      },
      {
        val: 'no',
        name: 'No — skip scenic route animations',
        desc: 'Disables the Round About and Detour scenic route animations for faster match pacing.'
      }
    ]
  },
  optCoach: {
    title: 'Coach Mode',
    subtitle: 'Real-time discard analysis and expected value projections:',
    options: [
      {
        val: 'off',
        name: 'Off — standard play',
        desc: 'Normal cribbage gameplay without hints or statistical projections.'
      },
      {
        val: 'on',
        name: 'On — show keep EV on discard',
        desc: 'Displays an interactive strategy projection banner during discard. Simulates every remaining possible cut card to calculate minimum, maximum, and expected average points for your hand.'
      }
    ]
  },
  optGoal: {
    title: 'Objective',
    subtitle: 'Choose whether you are racing to win or trying to avoid points:',
    options: [
      {
        val: 'high',
        name: 'High ball — first to target wins',
        desc: 'Classic cribbage objective. Race around the track—the first player to reach or exceed the target score wins immediately.'
      },
      {
        val: 'low',
        name: 'Low ball — first to target loses',
        desc: 'Reverse cribbage (misère)! The first player to reach the target score loses. Strategy is inverted: aim for 0-point hands, discard safe zeroes, and force your opponent to take points during pegging.'
      }
    ]
  },
  optTarget: {
    title: 'Target Score',
    subtitle: 'Select the winning score and game duration:',
    options: [
      {
        val: '121',
        name: 'Full Game (121)',
        desc: 'Standard regulation cribbage match to 121 points around the full serpentine board track.'
      },
      {
        val: '61',
        name: 'Quick game (61)',
        desc: 'Fast half-board match to 61 points, reaching the first half of the board for quick games.'
      },
      {
        val: '31',
        name: 'Pegging Only (31)',
        desc: 'Lightning-fast match to 31 points. Hands and cribs are not scored—every single point must be won during pegging play at the table!'
      },
      {
        val: '241',
        name: '241 - Short',
        desc: 'Exciting match to 241 peg holes across 4 streets (240 track holes plus the final game hole).'
      },
      {
        val: '361',
        name: '361 - Medium',
        desc: 'Strategic 6-street battle to 361 peg holes (360 track holes plus the final game hole).'
      },
      {
        val: '481',
        name: '481 - Long',
        desc: 'Epic marathon match to 481 peg holes on the massive 8-street serpentine board (480 track holes plus the game hole).'
      }
    ]
  },
  optDeal: {
    title: 'Deal',
    subtitle: 'Standard 4-card cribbage vs. Crazy Cribbage 6-card Super Deal:',
    options: [
      {
        val: 'std',
        name: 'Standard — 6 cards, discard 2, keep 4',
        desc: 'Traditional cribbage deal. Each player receives 6 cards, discards 2 into the crib, and keeps 4 cards in hand.'
      },
      {
        val: 'super',
        name: 'Super — 9 cards, discard 3, keep 6',
        desc: 'Crazy Cribbage signature mode! Each player receives 9 cards, discards 3 into a giant 6-card crib, and plays with a 6-card hand. Hands frequently score 30 to 50+ points!'
      }
    ]
  },
  optDealStyle: {
    title: 'Deal Animation',
    subtitle: 'Card dealing presentation and choreography:',
    options: [
      { val: 'alternate', name: 'Alternate — one to pone, one to dealer', desc: 'Classic card-by-card deal alternating between players.' },
      { val: 'packets', name: 'Packets — two at a time', desc: 'Deals cards in packets of two.' },
      { val: 'you-first', name: 'Your hand first, then theirs', desc: 'Deals your complete hand first, then the opponent\'s.' },
      { val: 'together', name: 'Together — both seats at once', desc: 'Deals to both seats simultaneously.' },
      { val: 'riffle', name: 'Riffle — same as alternate, fast', desc: 'Fast alternate deal.' },
      { val: 'waterfall', name: 'Waterfall — all cards, CSS stagger only', desc: 'Fast staggered card fan.' }
    ]
  },
  optShowDetail: {
    title: 'Show Points',
    subtitle: 'Control how scoring combinations are revealed at the end of each hand:',
    options: [
      {
        val: 'highlight',
        name: 'Highlight each scoring combo',
        desc: 'Paces through hand and crib scoring step-by-step, highlighting cards involved in 15s, pairs, runs, and flushes with visual combo callouts.'
      },
      {
        val: 'totals',
        name: 'Just show total points',
        desc: 'Skips combo-by-combo animations and immediately tallies total hand and crib points for faster, uninterrupted play.'
      },
      {
        val: 'muggins',
        name: 'Muggins (Cutthroat — manual count)',
        desc: 'Traditional cutthroat cribbage! You must manually count and claim your hand and crib points. If you undercount, your opponent can call Muggins and steal the forgotten points!'
      }
    ]
  },
  optDecks: {
    title: 'Decks',
    subtitle: 'Standard single deck vs. double deck madness:',
    options: [
      {
        val: '1',
        name: '1 deck — 52 cards',
        desc: 'Standard single 52-card deck (plus optional wild jokers).'
      },
      {
        val: '2',
        name: '2 decks — 104 cards',
        desc: 'Double deck with two of every card rank and suit! Enables 5-to-8 card runs, quadruple pairs, duplicate flushes, and monster hands.'
      }
    ]
  },
  optCardBack: {
    title: 'Card Back Patterns',
    subtitle: 'Available card back styles with custom textures and weaves:',
    options: [
      { val: 'classic-blue', name: 'Classic Blue', pattern: 'classic-blue', desc: 'Traditional casino crosshatch in deep navy.' },
      { val: 'classic-red', name: 'Classic Red', pattern: 'classic-red', desc: 'Bicycle crimson diamond weave in deep ruby.' },
      { val: 'emerald-diamond', name: 'Emerald Diamond', pattern: 'emerald-diamond', desc: 'Harlequin argyle green with gold flecks.' },
      { val: 'royal-gold', name: 'Royal Gold', pattern: 'royal-gold', desc: 'Luxurious onyx with gold filigree rosette mesh.' },
      { val: 'vintage-tartan', name: 'Vintage Tartan', pattern: 'vintage-tartan', desc: 'Scottish highland green, crimson and gold plaid.' },
      { val: 'honeycomb', name: 'Amber Honeycomb', pattern: 'honeycomb', desc: 'Warm geometric amber and espresso hexagon grid.' },
      { val: 'midnight-stars', name: 'Midnight Astral', pattern: 'midnight-stars', desc: 'Deep cosmic navy with starburst constellation dots.' },
      { val: 'amethyst', name: 'Amethyst Lattice', pattern: 'amethyst', desc: 'Royal imperial purple and lavender diamond weave.' },
      { val: 'vegas-crimson', name: 'Vegas Crimson', pattern: 'vegas-crimson', desc: 'Vibrant casino scarlet repeating mesh.' },
      { val: 'obsidian-carbon', name: 'Stealth Carbon', pattern: 'obsidian-carbon', desc: 'Modern charcoal and matte black carbon weave.' }
    ]
  }
};

export class DropdownHelpManager {
  /**
   * Opens the interactive info modal for the given dropdown option key.
   * @param {string} key
   * @param {Object} [context]
   */
  static showDropdownInfoModal(key, context = {}) {
    const config = DROPDOWN_DESCRIPTIONS[key];
    if (!config || typeof document === 'undefined') return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const showModal = context.showModal || (typeof window !== 'undefined' ? window.showModal : null);
    const getAppBrand = context.getAppBrand || (typeof window !== 'undefined' ? window.getAppBrand : () => 'crazy');

    let currVal = '';
    if (key === 'optCardBack') {
      currVal = settings.cardBack || 'classic-blue';
    } else {
      const el = (key === 'optBoardView' && document.getElementById('optBoardViewModal'))
        ? document.getElementById('optBoardViewModal')
        : document.getElementById(key);
      if (el) currVal = String(el.value);
    }

    let optionsList = config.options;
    if (key === 'optTarget') {
      const brand = typeof getAppBrand === 'function' ? getAppBrand() : (window.currentBrand || 'crazy');
      const isClash = brand === 'clash';
      const isCrazy = !brand || brand === 'crazy';
      if (isClash) {
        optionsList = optionsList.filter(opt => ['241', '361', '481'].includes(String(opt.val)));
      } else if (isCrazy) {
        optionsList = optionsList
          .filter(opt => ['121', '61', '31', '241'].includes(String(opt.val)))
          .map(opt => {
            if (String(opt.val) === '241') {
              return {
                ...opt,
                name: '241 points (There and Back)',
                desc: 'Epic extended match to 241 points (There and Back)—pegging twice the standard distance to the finish line!'
              };
            }
            return opt;
          });
      } else {
        optionsList = optionsList.filter(opt => ['121', '61', '31'].includes(String(opt.val)));
      }
    }

    const optionsHtml = optionsList.map(opt => {
      const isCurrent = String(opt.val) === currVal;
      return `
        <div class="opt-desc-card ${isCurrent ? 'is-active' : ''}" data-action="select-option" data-key="${key}" data-val="${opt.val}" role="button" tabindex="0" title="Click to select ${opt.name}">
          <div class="opt-card-header">
            <div class="opt-card-title">
              ${opt.pattern ? `<span class="pattern-preview pattern-${opt.pattern}" style="width:20px;height:28px;border-radius:3px;"></span>` : ''}
              <span>${opt.name}</span>
            </div>
            ${isCurrent 
              ? `<span class="opt-active-badge">Active</span>` 
              : `<span class="opt-select-pill">Select</span>`}
          </div>
          <div class="opt-card-body">${opt.desc}</div>
        </div>
      `;
    }).join('');

    const html = `
      <div class="dropdown-info-modal">
        <h2>${config.title}</h2>
        <div class="modal-sub">${config.subtitle}</div>
        <div class="dropdown-options-list">
          ${optionsHtml}
        </div>
        <div style="margin-top: 18px; text-align: center;">
          <button type="button" class="btn-gold modal-btn-close" id="btnCloseDropdownHelp">Close</button>
        </div>
      </div>
    `;

    if (typeof showModal === 'function') {
      showModal(html, true);
      if (typeof document !== 'undefined') {
        const modalRoot = (typeof document.querySelector === 'function' ? document.querySelector('.dropdown-info-modal') : null) || (typeof document.getElementById === 'function' ? document.getElementById('modal') : null);
        if (modalRoot && typeof modalRoot.addEventListener === 'function') {
          modalRoot.addEventListener('click', (e) => {
            const card = e.target.closest('[data-action="select-option"]');
            if (card) {
              const optKey = card.dataset.key;
              const optVal = card.dataset.val;
              if (typeof selectOptionFromModal === 'function') {
                selectOptionFromModal(optKey, optVal);
              } else if (DropdownHelpManager.selectOptionFromModal) {
                DropdownHelpManager.selectOptionFromModal(optKey, optVal);
              }
            }
          });
          modalRoot.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              const card = e.target.closest('[data-action="select-option"]');
              if (card) {
                e.preventDefault();
                const optKey = card.dataset.key;
                const optVal = card.dataset.val;
                if (typeof selectOptionFromModal === 'function') {
                  selectOptionFromModal(optKey, optVal);
                } else if (DropdownHelpManager.selectOptionFromModal) {
                  DropdownHelpManager.selectOptionFromModal(optKey, optVal);
                }
              }
            }
          });
        }
      }
    }
  }

  /**
   * Selects an option value from the modal and triggers change event.
   * @param {string} key
   * @param {string} val
   * @param {Object} [context]
   */
  static selectOptionFromModal(key, val, context = {}) {
    const hideModal = context.hideModal || (typeof window !== 'undefined' ? window.hideModal : () => {});
    const setCardBack = context.setCardBack || (typeof window !== 'undefined' ? window.setCardBack : () => {});

    if (key === 'optCardBack') {
      if (typeof setCardBack === 'function') {
        setCardBack(val);
      }
    } else if (typeof document !== 'undefined') {
      const el = document.getElementById(key);
      if (el) {
        el.value = val;
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (key === 'optBoardView') {
        const modalEl = document.getElementById('optBoardViewModal');
        if (modalEl) {
          modalEl.value = val;
          modalEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    if (typeof hideModal === 'function') {
      hideModal();
    }
  }

  /**
   * Injects info icon SVGs into option labels and attaches click/keyboard event listeners.
   * @param {Object} [context]
   */
  static initDropdownLabels(context = {}) {
    if (typeof document === 'undefined') return;

    const showModalFn = context.showDropdownInfoModal || (typeof window !== 'undefined' ? window.showDropdownInfoModal : null);

    document.querySelectorAll('.opt label').forEach(lbl => {
      let key = lbl.getAttribute('data-opt-key');
      if (!key) {
        const parent = lbl.closest('.opt');
        if (parent) {
          const sel = parent.querySelector('select');
          if (sel && sel.id) key = sel.id;
          else if (parent.querySelector('#cardBackPicker')) key = 'optCardBack';
        }
      }
      if (!key || !DROPDOWN_DESCRIPTIONS[key]) return;

      lbl.classList.add('clickable-opt-label');
      lbl.setAttribute('data-opt-key', key);
      lbl.setAttribute('role', 'button');
      lbl.setAttribute('tabindex', '0');
      lbl.setAttribute('aria-haspopup', 'dialog');
      lbl.title = 'Click to view details about ' + DROPDOWN_DESCRIPTIONS[key].title;

      const INFO_ICON_SVG = '<svg class="label-info-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle class="info-bg" cx="8" cy="8" r="7.2"/><circle class="info-mark" cx="8" cy="4.8" r="1.1" stroke="none"/><line class="info-mark" x1="8" y1="7.4" x2="8" y2="12" stroke-width="1.8" stroke-linecap="round"/></svg>';
      const existingIcon = lbl.querySelector('.label-info-icon');
      if (!existingIcon) {
        lbl.insertAdjacentHTML('beforeend', INFO_ICON_SVG);
      } else if (existingIcon.tagName.toLowerCase() !== 'svg') {
        existingIcon.outerHTML = INFO_ICON_SVG;
      }

      lbl.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof showModalFn === 'function') showModalFn(key);
      };

      lbl.onkeydown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          if (typeof showModalFn === 'function') showModalFn(key);
        }
      };
    });
  }
}
